import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listIntegrations, saveIntegration, deleteIntegration } from '../api/integrations';
import { Settings, CheckCircle, AlertCircle, Edit3, Trash2, Save, X, Plus, Copy, Maximize2, Info, FileCode, List, Layers, Lock, Globe, Users } from 'lucide-react';
import {
  MasterDetailLayout,
  MasterListItem,
  DetailPanelHeader,
  EmptyState
} from './MasterDetailLayout';
import DetailSection from "./detail/DetailSection";
import { FullScreenTextModal } from './FullScreenTextModal';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { cn } from '../lib/utils';
import { ConfirmDialog } from './ui/confirm-dialog';
import { fetchWorkgroupDetails, canAccessCddi } from './firestoreFunctions';
import { getUserId } from '../firebase';

type VisibilitySetting = 'public' | 'listed' | 'private';

const VISIBILITY_VALUES: readonly VisibilitySetting[] = ['public', 'listed', 'private'] as const;
const VISIBILITY_DISPLAY_ORDER: readonly VisibilitySetting[] = ['private', 'listed', 'public'] as const;

const normalizeVisibility = (value?: string): VisibilitySetting | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return (VISIBILITY_VALUES as readonly string[]).includes(normalized)
    ? (normalized as VisibilitySetting)
    : null;
};

const coerceVisibility = (value?: string): VisibilitySetting =>
  normalizeVisibility(value) ?? 'private';

const formatVisibilityLabel = (value: VisibilitySetting): string => {
  switch (value) {
    case 'public':
      return 'Public';
    case 'listed':
      return 'Listed';
    default:
      return 'Private';
  }
};

const VISIBILITY_OPTION_DESCRIPTIONS: Record<VisibilitySetting, string> = {
  public: 'Discoverable and installable by every workgroup in the realm.',
  listed: 'Appears in search and catalogs but must be explicitly enabled per workgroup.',
  private: 'Only visible to the owning workgroup and collaborators you invite.',
};

const getVisibilityBadgeVariant = (
  value: VisibilitySetting
): React.ComponentProps<typeof Badge>['variant'] => {
  switch (value) {
    case 'public':
      return 'default';
    case 'listed':
      return 'secondary';
    default:
      return 'outline';
  }
};

type WorkgroupOption = {
  id: string;
  name: string;
};

interface IntegrationDoc {
  id: string;
  name: string;
  system_prompt: string;
  cddi_instructions?: string;
  cddi_enabled?: boolean;
  cddi_project_name?: string;
  code_template?: string;
  data_fetch_functions?: string[];
  suggested_prompts?: string[];
  config_schema: Array<{ name: string; type?: string; is_secret?: boolean }>;
  created_at?: string;
  updated_at?: string;
  status?: VisibilitySetting | 'draft' | 'active' | 'inactive';
  description?: string;
  icon?: string;
  owner_type?: string;
  visibility?: VisibilitySetting;
  edit_access_workgroups?: string[];
  visibility_raw?: string;
  visibility_is_listed?: boolean;
  owner_id?: string;
  can_manage_integration?: boolean;
  can_edit_visibility?: boolean;
}

const emptyDoc: IntegrationDoc = {
  id: '',
  name: '',
  system_prompt: '',
  cddi_instructions: '',
  cddi_enabled: false,
  cddi_project_name: '',
  code_template: '',
  data_fetch_functions: [],
  suggested_prompts: [],
  config_schema: [],
  status: 'private',
  visibility: 'private',
  edit_access_workgroups: [],
  visibility_raw: 'private',
  visibility_is_listed: false,
  can_manage_integration: true,
  can_edit_visibility: true,
};

const DETAIL_BACKGROUND = "#e8effa";
const MAX_SUGGESTED_PROMPTS = 4;

const IntegrationManagerNew: React.FC = () => {
  const [integrations, setIntegrations] = useState<IntegrationDoc[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<IntegrationDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dataFetchInput, setDataFetchInput] = useState('');
  const dataFetchInputRef = useRef<HTMLInputElement | null>(null);
  const [fullScreenModal, setFullScreenModal] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
    editable?: boolean;
    onSave?: (newContent: string) => void;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibilityConfirmOpen, setVisibilityConfirmOpen] = useState(false);
  const [pendingVisibility, setPendingVisibility] = useState<VisibilitySetting | null>(null);
  const [workgroupOptions, setWorkgroupOptions] = useState<WorkgroupOption[]>([]);
  const [workgroupsLoading, setWorkgroupsLoading] = useState(false);
  const [workgroupsError, setWorkgroupsError] = useState<string | null>(null);
  const [workgroupSearch, setWorkgroupSearch] = useState('');
  const [workgroupDisplayOrder, setWorkgroupDisplayOrder] = useState<string[]>([]);
  const [hasCddiAccess, setHasCddiAccess] = useState(false);

  const selectedIntegration = integrations.find(i => i.id === selectedId);
  const normalizedWorkgroupSearch = workgroupSearch.trim().toLowerCase();
  const workgroupOptionMap = useMemo(() => {
    const map = new Map<string, WorkgroupOption>();
    for (const option of workgroupOptions) {
      map.set(option.id, option);
    }
    return map;
  }, [workgroupOptions]);

  const filteredWorkgroupOptions = useMemo(() => {
    if (normalizedWorkgroupSearch.length === 0) {
      return workgroupOptions;
    }
    return workgroupOptions.filter((workgroup) => {
      const name = (workgroup.name || '').toLowerCase();
      const id = (workgroup.id || '').toLowerCase();
      return name.includes(normalizedWorkgroupSearch) || id.includes(normalizedWorkgroupSearch);
    });
  }, [normalizedWorkgroupSearch, workgroupOptions]);

  const filteredWorkgroupIds = useMemo(() => {
    const ids = new Set<string>();
    for (const option of filteredWorkgroupOptions) {
      ids.add(option.id);
    }
    return ids;
  }, [filteredWorkgroupOptions]);

  const orderedFilteredWorkgroups = useMemo(() => {
    if (workgroupDisplayOrder.length > 0) {
      const ordered: WorkgroupOption[] = [];
      for (const id of workgroupDisplayOrder) {
        if (!filteredWorkgroupIds.has(id)) continue;
        const option = workgroupOptionMap.get(id);
        if (option) ordered.push(option);
      }
      const missing = filteredWorkgroupOptions.filter((option) => !workgroupDisplayOrder.includes(option.id));
      return ordered.concat(missing);
    }
    return filteredWorkgroupOptions;
  }, [filteredWorkgroupOptions, filteredWorkgroupIds, workgroupDisplayOrder, workgroupOptionMap]);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listIntegrations();
      const normalizedList = (list || []).map((item: IntegrationDoc) => {
        const normalizedVisibility = coerceVisibility(item.visibility);
        const normalizedStatus = normalizeVisibility(item.status) ?? normalizedVisibility;
        const canManageIntegration = item.can_manage_integration !== false;
        const canEditVisibility = item.can_edit_visibility !== false && canManageIntegration;
        const rawVisibility = normalizeVisibility(item.visibility_raw) ?? normalizedVisibility;
        const isListed = item.visibility_is_listed ?? rawVisibility === 'listed';
        return {
          ...item,
          cddi_enabled: item.cddi_enabled ?? false,
          cddi_project_name: item.cddi_project_name || '',
          visibility: normalizedVisibility,
          status: normalizedStatus,
          edit_access_workgroups: Array.isArray(item.edit_access_workgroups)
            ? item.edit_access_workgroups
            : [],
          visibility_raw: rawVisibility,
          visibility_is_listed: isListed,
          can_manage_integration: canManageIntegration,
          can_edit_visibility: canEditVisibility,
        };
      });
      setIntegrations(normalizedList);
    } catch (e: any) {
      setError(e.message || 'Failed to fetch integrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const evaluateCddiAccess = async () => {
      try {
        const userId = await getUserId();
        if (!userId) {
          if (!cancelled) {
            setHasCddiAccess(false);
          }
          return;
        }
        const allowed = await canAccessCddi(userId);
        if (!cancelled) {
          setHasCddiAccess(Boolean(allowed));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('IntegrationManagerNew: failed to check CDDI access', error);
          setHasCddiAccess(false);
        }
      }
    };

    evaluateCddiAccess();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadWorkgroups = async () => {
      setWorkgroupsLoading(true);
      try {
        const userId = await getUserId();
        if (!isMounted) return;
        if (!userId) {
          setWorkgroupOptions([]);
          setWorkgroupsError('You must be signed in to manage edit access.');
          return;
        }

        const workgroups = await fetchWorkgroupDetails(userId);
        if (!isMounted) return;
        const options: WorkgroupOption[] = (workgroups || [])
          .map((group) => ({
            id: group.id,
            name: group.name || group.id,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setWorkgroupOptions(options);
        setWorkgroupsError(null);
      } catch (error) {
        if (!isMounted) return;
        console.error('IntegrationManagerNew: failed to load workgroups', error);
        setWorkgroupOptions([]);
        setWorkgroupsError('Failed to load your workgroups');
      } finally {
        if (!isMounted) return;
        setWorkgroupsLoading(false);
      }
    };

    loadWorkgroups();

    return () => {
      isMounted = false;
    };
  }, []);

  const slugify = (value: string): string => {
    if (!value) return '';
    return value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
  };

  const validateSchema = (schema: IntegrationDoc['config_schema']): {
    valid: boolean;
    error?: string;
    duplicates?: Set<string>
  } => {
    const names = (schema || []).map((f) => (f.name || '').trim().toLowerCase());
    if (names.some((n) => !n)) {
      return { valid: false, error: 'All config fields must have a non-empty name.' };
    }
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const n of names) {
      if (seen.has(n)) dupes.add(n);
      seen.add(n);
    }
    if (dupes.size > 0) {
      return {
        valid: false,
        error: `Duplicate field names: ${Array.from(dupes).join(', ')}`,
        duplicates: dupes
      };
    }
    return { valid: true };
  };

  const computeDisplayOrder = useCallback((selectedIds: string[]) => {
    const selectedSet = new Set(selectedIds);
    const selected = workgroupOptions
      .filter((option) => selectedSet.has(option.id))
      .map((option) => option.id);
    const unselected = workgroupOptions
      .filter((option) => !selectedSet.has(option.id))
      .map((option) => option.id);
    setWorkgroupDisplayOrder([...new Set([...selected, ...unselected])]);
  }, [workgroupOptions]);

  const startNew = () => {
    setEditing({ ...emptyDoc });
    setSelectedId(null);
    setPendingVisibility(null);
    setVisibilityConfirmOpen(false);
    setWorkgroupSearch('');
    if (workgroupOptions.length > 0) {
      setWorkgroupDisplayOrder(workgroupOptions.map((option) => option.id));
    } else {
      setWorkgroupDisplayOrder([]);
    }
  };

  const startEdit = (doc: IntegrationDoc) => {
    const normalizedVisibility = coerceVisibility(doc.visibility);
    const normalizedStatus = normalizeVisibility(doc.status) ?? normalizedVisibility;
    setEditing({
      ...doc,
      cddi_enabled: doc.cddi_enabled ?? false,
      cddi_project_name: doc.cddi_project_name || '',
      suggested_prompts: Array.isArray(doc.suggested_prompts) ? doc.suggested_prompts : [],
      visibility: normalizedVisibility,
      status: normalizedStatus,
      edit_access_workgroups: Array.isArray(doc.edit_access_workgroups)
        ? doc.edit_access_workgroups
        : [],
      can_manage_integration: doc.can_manage_integration !== false,
      can_edit_visibility:
        doc.can_manage_integration !== false && doc.can_edit_visibility !== false,
    });
    setSelectedId(doc.id);
    setPendingVisibility(null);
    setVisibilityConfirmOpen(false);
    setWorkgroupSearch('');
    if (workgroupOptions.length > 0) {
      computeDisplayOrder(
        Array.isArray(doc.edit_access_workgroups) ? doc.edit_access_workgroups : []
      );
    } else {
      setWorkgroupDisplayOrder([]);
    }
  };

  const cancelEdit = () => {
    setEditing(null);
    setError(null);
    setPendingVisibility(null);
    setVisibilityConfirmOpen(false);
    setWorkgroupSearch('');
    setWorkgroupDisplayOrder([]);
  };

  useEffect(() => {
    if (!editing) return;
    if (workgroupDisplayOrder.length > 0) return;
    computeDisplayOrder(
      Array.isArray(editing.edit_access_workgroups)
        ? editing.edit_access_workgroups
        : []
    );
  }, [editing, workgroupDisplayOrder.length, computeDisplayOrder]);

  const requestVisibilityChange = (nextVisibility: VisibilitySetting) => {
    if (
      !editing ||
      nextVisibility === editing.visibility ||
      editing.can_edit_visibility === false
    ) {
      return;
    }
    if (nextVisibility === 'public') {
      setPendingVisibility('public');
      setVisibilityConfirmOpen(true);
      return;
    }
    setEditing((prev) =>
      prev ? { ...prev, visibility: nextVisibility, status: nextVisibility } : prev
    );
  };

  const updateEditAccessWorkgroups = (workgroupId: string, enabled: boolean) => {
    setEditing((prev) => {
      if (!prev) return prev;
      if (prev.can_edit_visibility === false) return prev;
      const current = Array.isArray(prev.edit_access_workgroups)
        ? prev.edit_access_workgroups
        : [];
      const next = enabled
        ? Array.from(new Set([...current, workgroupId]))
        : current.filter((id) => id !== workgroupId);
      return { ...prev, edit_access_workgroups: next };
    });
  };

  const handleVisibilityConfirm = () => {
    if (pendingVisibility && editing && editing.can_edit_visibility !== false) {
      setEditing((prev) =>
        prev
          ? { ...prev, visibility: pendingVisibility, status: pendingVisibility }
          : prev
      );
    }
    setPendingVisibility(null);
    setVisibilityConfirmOpen(false);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    setError(null);
    try {
      const validation = validateSchema(editing.config_schema || []);
      if (!validation.valid) {
        setError(validation.error || 'Invalid config schema');
        return;
      }
      const {
        can_manage_integration: _canManageIntegration,
        can_edit_visibility: _canEditVisibility,
        visibility_raw: _visibilityRaw,
        visibility_is_listed: _visibilityIsListed,
        ...rest
      } = editing;
      const visibility = coerceVisibility(editing.visibility);
      const cddiEnabled = Boolean(editing.cddi_enabled);
      const payload: IntegrationDoc = {
        ...rest,
        cddi_enabled: cddiEnabled,
        cddi_project_name: (editing.cddi_project_name || '').trim(),
        data_fetch_functions: Array.isArray(editing.data_fetch_functions)
          ? editing.data_fetch_functions
          : [],
        suggested_prompts: Array.isArray(editing.suggested_prompts)
          ? editing.suggested_prompts
              .map((prompt) => (prompt || '').trim())
              .filter((prompt) => prompt.length > 0)
              .slice(0, MAX_SUGGESTED_PROMPTS)
          : [],
        status: visibility,
        visibility,
        edit_access_workgroups: Array.isArray(editing.edit_access_workgroups)
          ? editing.edit_access_workgroups
          : [],
      };
      await saveIntegration(payload);
      setSuccess(`Integration "${editing.name}" saved successfully`);
      setEditing(null);
      setWorkgroupSearch('');
      await refresh();
      setSelectedId(payload.id);
      setPendingVisibility(null);
      setVisibilityConfirmOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setError(null);
    try {
      await deleteIntegration(id);
      setSuccess(`Integration "${name}" deleted`);
      if (selectedId === id) {
        setSelectedId(null);
        setEditing(null);
      }
      await refresh();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to delete integration');
    }
  };

  const addDataFetchFunctions = (raw: string) => {
    if (!editing) return;
    const val = (raw || '').trim();
    if (!val) return;
    const sanitized = val
      .split(',')
      .map(v => v.trim())
      .filter(v => v.length > 0);
    if (sanitized.length === 0) return;
    const next = Array.from(new Set([...(editing.data_fetch_functions || []), ...sanitized]));
    setEditing({ ...editing, data_fetch_functions: next });
    setDataFetchInput('');
    if (dataFetchInputRef.current) dataFetchInputRef.current.value = '';
  };

  const getIntegrationIcon = (integration: IntegrationDoc): string => {
    const iconMap: Record<string, string> = {
      'aws': '☁️',
      'maximo': '🏭',
      'salesforce': '☁️',
      'woocommerce': '🛒',
      'hubspot': '🔶',
      'microsoft': '🪟',
    };
    return integration.icon || iconMap[integration.id.toLowerCase()] || '⚙️';
  };

  const getVisibilityIcon = (visibility?: string) => {
    const value = coerceVisibility(visibility);
    switch (value) {
      case 'public':
        return Globe;
      case 'listed':
        return List;
      default:
        return Lock;
    }
  };

  const getVisibilityColor = (visibility?: string) => {
    const value = coerceVisibility(visibility);
    switch (value) {
      case 'public':
        return 'text-green-600';
      case 'listed':
        return 'text-sky-600';
      default:
        return 'text-gray-600';
    }
  };

  // Render Master List
  const renderMasterList = () => {
    if (loading) {
      return (
        <div className="p-6 text-center text-muted-foreground">
          Loading integrations...
        </div>
      );
    }

    if (integrations.length === 0) {
      return (
        <div className="p-6 text-center">
          <p className="text-sm text-muted-foreground mb-4">No integrations yet</p>
          <Button onClick={startNew} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Create First Integration
          </Button>
        </div>
      );
    }

    // Filter integrations based on search query
    const filteredIntegrations = searchQuery.trim()
      ? integrations.filter((integration) => {
          const query = searchQuery.toLowerCase();
          const visibilityRaw = normalizeVisibility(integration.visibility_raw);
          const visibilityMatch = integration.visibility?.toLowerCase().includes(query);
          const listedMatch =
            (integration.visibility_is_listed || visibilityRaw === 'listed') &&
            'listed'.includes(query);
          return (
            integration.id.toLowerCase().includes(query) ||
            integration.name?.toLowerCase().includes(query) ||
            integration.description?.toLowerCase().includes(query) ||
            visibilityMatch ||
            listedMatch
          );
        })
      : integrations;

    if (filteredIntegrations.length === 0) {
      return (
        <div className="p-6 text-center">
          <p className="text-sm text-muted-foreground">No integrations match "{searchQuery}"</p>
        </div>
      );
    }

    return (
      <div>
        {filteredIntegrations.map((integration) => {
          const listVisibility = coerceVisibility(integration.visibility);
          const listVisibilityRaw = normalizeVisibility(integration.visibility_raw);
          const listIsListed = integration.visibility_is_listed ?? listVisibilityRaw === 'listed';
          const VisibilityGlyph = getVisibilityIcon(listVisibility);
          const badgeLabel =
            listVisibility === 'private' && listIsListed
              ? 'Private (Listed)'
              : formatVisibilityLabel(listVisibility);
          return (
            <MasterListItem
              key={integration.id}
              title={integration.name || integration.id}
              subtitle={integration.created_at ? new Date(integration.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : `ID: ${integration.id}`}
              icon={<span className="text-2xl">{getIntegrationIcon(integration)}</span>}
              isSelected={selectedId === integration.id}
              onClick={() => {
                setSelectedId(integration.id);
                setEditing(null);
              }}
              badge={
                <Badge
                  variant={getVisibilityBadgeVariant(listVisibility)}
                  className="flex items-center gap-1"
                >
                  <VisibilityGlyph className={cn('h-3 w-3', getVisibilityColor(listVisibility))} />
                  {badgeLabel}
                </Badge>
              }
            />
          );
        })}
      </div>
    );
  };

  // Render Detail Panel
  const renderDetailPanel = () => {
    // Show edit form
    if (editing) {
      return renderEditForm();
    }

    // Show selected integration details
    if (selectedIntegration) {
      return renderIntegrationDetails(selectedIntegration);
    }

    // Empty state
    return (
      <EmptyState
        icon={<Settings className="w-8 h-8 text-muted-foreground" />}
        title="No Integration Selected"
        description="Select an integration from the sidebar to view details"
        action={
          <Button onClick={startNew}>
            <Plus className="w-4 h-4 mr-2" />
            New Integration
          </Button>
        }
      />
    );
  };

  // Render Integration Details (View Mode)
  const renderIntegrationDetails = (integration: IntegrationDoc) => {
    const visibility = coerceVisibility(integration.visibility);
    const visibilityRaw = normalizeVisibility(integration.visibility_raw);
    const visibilityIsListed = integration.visibility_is_listed ?? visibilityRaw === 'listed';
    const VisibilityIcon = getVisibilityIcon(visibility);
    const canManageIntegration = integration.can_manage_integration !== false;
    const cddiEnabled = Boolean(integration.cddi_enabled);
    const showCddiDetails = hasCddiAccess && cddiEnabled;
    const overviewItems = [
      { label: "Name", value: integration.name || integration.id },
      { label: "Integration ID", value: integration.id },
      {
        label: "Created",
        value: integration.created_at
          ? new Date(integration.created_at).toLocaleString()
          : "—",
      },
      {
        label: "Updated",
        value: integration.updated_at
          ? new Date(integration.updated_at).toLocaleString()
          : "—",
      },
      {
        label: "Visibility",
        value:
          visibility === 'private' && visibilityIsListed
            ? 'Private (Listed)'
            : formatVisibilityLabel(visibility),
      },
    ];

    if (hasCddiAccess) {
      overviewItems.push({
        label: "CDDI",
        value: cddiEnabled ? "Enabled" : "Disabled",
      });

      if (cddiEnabled && integration.cddi_project_name) {
        overviewItems.push({
          label: "CDDI project name",
          value: integration.cddi_project_name,
        });
      }
    }

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: DETAIL_BACKGROUND }}>
        <DetailPanelHeader
          title="Integration Details"
          subtitle="Review metadata, prompts, and configuration."
          actions={
            <>
              <Button
                data-testid="integration-edit-btn"
                onClick={() => {
                  if (!canManageIntegration) return;
                  startEdit(integration);
                }}
                variant="outline"
                size="sm"
                disabled={!canManageIntegration}
                className={!canManageIntegration ? 'opacity-50 cursor-not-allowed' : undefined}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                data-testid="integration-delete-btn"
                onClick={() => handleDelete(integration.id, integration.name)}
                variant="destructive"
                size="sm"
                disabled={!canManageIntegration}
                className={!canManageIntegration ? 'opacity-50 cursor-not-allowed' : undefined}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </>
          }
        />

        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-8">
            <DetailSection
              icon={<Info className="h-5 w-5" />}
              title="Overview"
              subtitle="Key metadata powering this integration."
              toolbar={
                <Badge
                  variant={getVisibilityBadgeVariant(visibility)}
                  className="flex items-center gap-1"
                >
                  <VisibilityIcon className={cn("h-3.5 w-3.5", getVisibilityColor(visibility))} />
                  {visibility === 'private' && visibilityIsListed
                    ? 'Private (Listed)'
                    : formatVisibilityLabel(visibility)}
                </Badge>
              }
            >
              <div className="grid gap-4 sm:grid-cols-2">
                {overviewItems.map((item) => (
                  <div key={item.label}>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {item.label}
                    </p>
                    <p className="text-sm text-foreground/80 break-words">
                      {item.value || "—"}
                    </p>
                  </div>
                ))}
              </div>
              {integration.description && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Description
                  </p>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {integration.description}
                  </p>
                </div>
              )}
              {visibility === 'public' && (
                <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  This integration is public. All workgroups in the realm can discover and use it.
                </div>
              )}
              {visibility === 'listed' && (
                <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-sky-700">
                  Listed integrations appear in catalogs but still require individual workgroups to enable them.
                </div>
              )}
              {visibility === 'private' && (
                <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                  Private integrations remain hidden from other workgroups unless explicitly shared.
                  {visibilityIsListed && (
                    <span className="block pt-2 text-xs text-muted-foreground">
                      Because this integration is listed, administrators can still discover it in the enablement catalog for enablement purposes.
                    </span>
                  )}
                </div>
              )}
            </DetailSection>

            {Array.isArray(integration.edit_access_workgroups) &&
              integration.edit_access_workgroups.length > 0 && (
                <DetailSection
                  icon={<Users className="h-5 w-5" />}
                  title="Edit access"
                  subtitle="Workgroups that can edit this integration."
                >
                  <div className="flex flex-wrap gap-2">
                    {integration.edit_access_workgroups.map((workgroupId) => {
                      const friendlyName =
                        workgroupOptions.find((option) => option.id === workgroupId)?.name ||
                        workgroupId;
                      return (
                        <Badge key={workgroupId} variant="secondary">
                          {friendlyName}
                        </Badge>
                      );
                    })}
                  </div>
                </DetailSection>
              )}

            {integration.system_prompt && (
              <DetailSection
                icon={<FileCode className="h-5 w-5" />}
                title="System prompt"
                subtitle="Baseline instructions used by the integration."
                toolbar={
                  <div className="flex gap-2">
                    <Button
                      data-testid="system-prompt-maximize-btn"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFullScreenModal({
                          isOpen: true,
                          title: "System Prompt",
                          content: integration.system_prompt,
                        })
                      }
                    >
                      <Maximize2 className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid="system-prompt-copy-btn"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          if (navigator?.clipboard?.writeText) {
                            await navigator.clipboard.writeText(integration.system_prompt);
                          }
                        } catch (e) {
                          console.error("Copy failed:", e);
                        }
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                }
              >
                <Textarea
                  data-testid="system-prompt-textarea"
                  readOnly
                  value={integration.system_prompt}
                  className="min-h-56 max-h-96 whitespace-pre-wrap break-words font-mono text-xs"
                />
              </DetailSection>
            )}

            {showCddiDetails && (
              <DetailSection
                icon={<Info className="h-5 w-5" />}
                title="CDDI"
                subtitle="Challenge-driven intelligence configuration."
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Status
                    </p>
                    <p className="text-sm text-foreground/80">Enabled</p>
                  </div>
                  {integration.cddi_project_name && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Project name
                      </p>
                      <p className="text-sm text-foreground/80 break-words">
                        {integration.cddi_project_name}
                      </p>
                    </div>
                  )}
                </div>

                {integration.cddi_instructions && (
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">CDDI instructions</h4>
                      <div className="flex gap-2">
                        <Button
                          data-testid="cddi-instructions-maximize-btn"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setFullScreenModal({
                              isOpen: true,
                              title: "CDDI Instructions",
                              content: integration.cddi_instructions || "",
                            })
                          }
                        >
                          <Maximize2 className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid="cddi-instructions-copy-btn"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              if (navigator?.clipboard?.writeText) {
                                await navigator.clipboard.writeText(
                                  integration.cddi_instructions || ""
                                );
                              }
                            } catch (e) {
                              console.error("Copy failed:", e);
                            }
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <Textarea
                      data-testid="cddi-instructions-textarea"
                      readOnly
                      value={integration.cddi_instructions}
                      className="min-h-40 max-h-96 whitespace-pre-wrap break-words font-mono text-xs"
                    />
                  </div>
                )}

                {integration.data_fetch_functions &&
                  integration.data_fetch_functions.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h4 className="text-sm font-medium">Data fetch functions</h4>
                      <div className="flex flex-wrap gap-2">
                        {integration.data_fetch_functions.map((fn, idx) => (
                          <Badge key={idx} variant="secondary">
                            {fn}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
              </DetailSection>
            )}

            {integration.code_template && (
              <DetailSection
                icon={<FileCode className="h-5 w-5" />}
                title="Code template"
                subtitle="Reference implementation used by the integration."
                toolbar={
                  <div className="flex gap-2">
                    <Button
                      data-testid="code-template-maximize-btn"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFullScreenModal({
                          isOpen: true,
                          title: "Code Template",
                          content: integration.code_template || "",
                        })
                      }
                    >
                      <Maximize2 className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid="code-template-copy-btn"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          if (navigator?.clipboard?.writeText) {
                            await navigator.clipboard.writeText(
                              integration.code_template || ""
                            );
                          }
                        } catch (e) {
                          console.error("Copy failed:", e);
                        }
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                }
              >
                <Textarea
                  data-testid="code-template-textarea"
                  readOnly
                  value={integration.code_template}
                  className="min-h-56 max-h-96 whitespace-pre-wrap break-words font-mono text-xs"
                />
              </DetailSection>
            )}

            <DetailSection
              icon={<Layers className="h-5 w-5" />}
              title="Configuration schema"
              subtitle="Fields required to authenticate and connect this integration."
            >
              {integration.config_schema && integration.config_schema.length > 0 ? (
                <div className="overflow-hidden rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Field</th>
                        <th className="px-4 py-2 text-left font-medium">Type</th>
                        <th className="px-4 py-2 text-left font-medium">Secret</th>
                      </tr>
                    </thead>
                    <tbody>
                      {integration.config_schema.map((field, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-4 py-2 font-medium">{field.name}</td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {field.type || "string"}
                          </td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {field.is_secret ? "Yes" : "No"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No configuration schema defined yet.
                </p>
              )}
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  // Render Edit Form
  const renderEditForm = () => {
    if (!editing) return null;
    const currentVisibility = coerceVisibility(editing.visibility);
    const selectedEditWorkgroups = editing.edit_access_workgroups || [];
    const canEditVisibility = editing.can_edit_visibility !== false;
    const canManageIntegration = editing.can_manage_integration !== false;
    const orderedWorkgroups = orderedFilteredWorkgroups;
    const isCddiEnabled = Boolean(editing.cddi_enabled);
    const showCddiSection = hasCddiAccess;

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: DETAIL_BACKGROUND }}>
        <DetailPanelHeader
          title={editing.id ? "Edit Integration" : "New Integration"}
          subtitle={
            editing.id ? editing.name || editing.id : "Define the integration settings."
          }
          actions={
            <>
              <Button onClick={cancelEdit} variant="outline" size="sm">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !canManageIntegration} size="sm">
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save"}
              </Button>
            </>
          }
        />

        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-8">
            <DetailSection
              icon={<Info className="h-5 w-5" />}
              title="Integration overview"
              subtitle="These values identify how the integration appears across the product."
            >
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium">
                    Integration ID <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={editing.id}
                    onChange={(e) =>
                      setEditing({ ...editing, id: slugify(e.target.value) })
                    }
                    placeholder="e.g., aws, maximo, salesforce"
                    className="w-full rounded-md border px-3 py-2"
                    disabled={!!selectedIntegration}
                  />
                  <p className="text-xs text-muted-foreground">
                    Lowercase letters, numbers, and underscores only.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium">
                    Display Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={editing.name}
                    onChange={(e) =>
                      setEditing({ ...editing, name: e.target.value })
                    }
                    placeholder="e.g., Amazon Web Services"
                    className="w-full rounded-md border px-3 py-2"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="block text-sm font-medium">Description</label>
                  <input
                    type="text"
                    value={editing.description || ""}
                    onChange={(e) =>
                      setEditing({ ...editing, description: e.target.value })
                    }
                    placeholder="Brief description of this integration"
                    className="w-full rounded-md border px-3 py-2"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <span className="block text-sm font-medium">Visibility</span>
                  <div className="grid gap-3 md:grid-cols-3">
                    {VISIBILITY_DISPLAY_ORDER.map((option) => {
                      const isActive = currentVisibility === option;
                      const Icon = getVisibilityIcon(option);
                      const disabled = !canEditVisibility;
                      return (
                        <button
                          key={option}
                          type="button"
                          aria-pressed={isActive}
                          onClick={() => requestVisibilityChange(option)}
                          className={cn(
                            "flex h-full flex-col items-start rounded-md border px-3 py-3 text-left transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                            isActive
                              ? "border-primary bg-primary/5"
                              : "border-slate-200 hover:border-primary/40",
                            disabled ? "cursor-not-allowed opacity-50" : undefined
                          )}
                          disabled={disabled}
                        >
                          <span className="flex items-center gap-2 text-sm font-medium">
                            <Icon className={cn("h-4 w-4", getVisibilityColor(option))} />
                            {formatVisibilityLabel(option)}
                          </span>
                          <span className="mt-2 text-xs text-muted-foreground">
                            {VISIBILITY_OPTION_DESCRIPTIONS[option]}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Choose how the integration is surfaced to other workgroups.
                  </p>
                  {!canEditVisibility && (
                    <p className="text-xs text-muted-foreground">
                      Only the integration owner can change visibility settings.
                    </p>
                  )}
                  {currentVisibility === 'public' && (
                    <p className="text-xs font-medium text-destructive">
                      Public integrations can be used by anyone in the realm.
                    </p>
                  )}
                  {currentVisibility === 'listed' && (
                    <p className="text-xs text-sky-700">
                      Listed integrations are discoverable but still require workgroups to enable them.
                    </p>
                  )}
                  {currentVisibility === 'private' && (
                    <p className="text-xs text-muted-foreground">
                      Private integrations remain visible only to your team.
                    </p>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    Edit access workgroups
                  </span>
                  <p className="text-xs text-muted-foreground">
                    Owners can give additional workgroups edit permissions for this integration.
                  </p>
                  {workgroupOptions.length > 0 && (
                    <div className="relative">
                      <input
                        type="text"
                        value={workgroupSearch}
                        onChange={(event) => setWorkgroupSearch(event.target.value)}
                        placeholder="Search workgroups..."
                        className={cn(
                          "mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50",
                          !canEditVisibility && "cursor-not-allowed bg-muted text-muted-foreground"
                        )}
                        disabled={!canEditVisibility}
                      />
                    </div>
                  )}
                  {workgroupsLoading ? (
                    <div className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
                      Loading your workgroups...
                    </div>
                  ) : workgroupOptions.length === 0 ? (
                    <div className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
                      You are not a member of any other workgroups yet.
                    </div>
                  ) : filteredWorkgroupOptions.length === 0 ? (
                    <div className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
                      No workgroups match "{workgroupSearch.trim()}".
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
                      {orderedWorkgroups.map((workgroup) => {
                        const checked = selectedEditWorkgroups.includes(workgroup.id);
                        return (
                          <label
                            key={workgroup.id}
                            className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                          >
                            <span>
                              <span className="block font-medium text-foreground">
                                {workgroup.name}
                              </span>
                              <span className="block text-xs text-muted-foreground">
                                {workgroup.id}
                              </span>
                            </span>
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={checked}
                              onChange={(event) =>
                                updateEditAccessWorkgroups(workgroup.id, event.target.checked)
                              }
                              aria-label={`Toggle edit access for ${workgroup.name}`}
                              disabled={!canEditVisibility}
                            />
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {workgroupsError && (
                    <p className="text-xs text-destructive">
                      {workgroupsError}
                    </p>
                  )}
                  {!canEditVisibility && (
                    <p className="text-xs text-muted-foreground">
                      Only the integration owner can change edit access.
                    </p>
                  )}
                </div>
              </div>
            </DetailSection>

            <DetailSection
              icon={<FileCode className="h-5 w-5" />}
              title="Prompts & templates"
              subtitle="Author prompts and templates that the integration relies on."
            >
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium">
                      System Prompt <span className="text-destructive">*</span>
                    </label>
                    <div className="flex gap-2">
                      <Button
                        data-testid="system-prompt-maximize-btn"
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() =>
                          setFullScreenModal({
                            isOpen: true,
                            title: "System Prompt",
                            content: editing.system_prompt,
                            editable: true,
                            onSave: (newContent) => {
                              setEditing({ ...editing, system_prompt: newContent });
                              setFullScreenModal(null);
                            },
                          })
                        }
                      >
                        <Maximize2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    value={editing.system_prompt}
                    onChange={(e) =>
                      setEditing({ ...editing, system_prompt: e.target.value })
                    }
                    placeholder="Enter the system prompt for the LLM..."
                    rows={6}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium">
                      Code Template (Optional)
                    </label>
                    <Button
                      data-testid="code-template-maximize-btn"
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={() =>
                        setFullScreenModal({
                          isOpen: true,
                          title: "Code Template",
                          content: editing.code_template || "",
                          editable: true,
                          onSave: (newContent) => {
                            setEditing({
                              ...editing,
                              code_template: newContent,
                            });
                            setFullScreenModal(null);
                          },
                        })
                      }
                    >
                      <Maximize2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Textarea
                    value={editing.code_template || ""}
                    onChange={(e) =>
                      setEditing({ ...editing, code_template: e.target.value })
                    }
                    placeholder="Python code template..."
                    rows={8}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </DetailSection>

            {showCddiSection && (
              <DetailSection
                icon={<Info className="h-5 w-5" />}
                title="CDDI"
                subtitle="Control challenge-driven intelligence settings for this integration."
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-3 rounded-md border border-dashed border-border/70 bg-muted/20 p-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium">
                        CDDI Enabled
                      </label>
                      <p className="text-xs text-muted-foreground">
                        Toggle challenge-driven intelligence features for this integration.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-sm font-medium">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isCddiEnabled}
                        onClick={() =>
                          setEditing({
                            ...editing,
                            cddi_enabled: !isCddiEnabled,
                          })
                        }
                        className={cn(
                          "relative inline-flex h-6 w-11 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40",
                          isCddiEnabled
                            ? "bg-primary border-primary"
                            : "bg-muted text-muted-foreground border-border"
                        )}
                      >
                        <span
                          className={cn(
                            "inline-block h-4 w-4 transform rounded-full bg-white shadow transition",
                            isCddiEnabled ? "translate-x-5" : "translate-x-1"
                          )}
                        />
                      </button>
                      <span className="text-muted-foreground">
                        {isCddiEnabled ? "On" : "Off"}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium">
                      CDDI Project Name
                    </label>
                    <input
                      type="text"
                      value={editing.cddi_project_name || ""}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          cddi_project_name: e.target.value,
                        })
                      }
                      placeholder="e.g., plant-ops-cddi"
                      className={cn(
                        "w-full rounded-md border px-3 py-2 text-sm",
                        !isCddiEnabled && "cursor-not-allowed bg-muted text-muted-foreground"
                      )}
                      disabled={!isCddiEnabled}
                    />
                    <p className="text-xs text-muted-foreground">
                      Name of the project in the CDDI server.
                    </p>
                  </div>

                  {isCddiEnabled && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium">
                            CDDI Instructions (Optional)
                          </label>
                          <Button
                            data-testid="cddi-instructions-maximize-btn"
                            variant="outline"
                            size="sm"
                            type="button"
                            onClick={() =>
                              setFullScreenModal({
                                isOpen: true,
                                title: "CDDI Instructions",
                                content: editing.cddi_instructions || "",
                                editable: true,
                                onSave: (newContent) => {
                                  setEditing({
                                    ...editing,
                                    cddi_instructions: newContent,
                                  });
                                  setFullScreenModal(null);
                                },
                              })
                            }
                          >
                            <Maximize2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Textarea
                          value={editing.cddi_instructions || ""}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              cddi_instructions: e.target.value,
                            })
                          }
                          placeholder="Challenge-driven database intelligence instructions..."
                          rows={4}
                          className="font-mono text-sm"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium">
                            Data fetch functions
                          </label>
                          <span className="text-xs text-muted-foreground">
                            Add helpers referenced by CDDI jobs.
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <input
                            ref={dataFetchInputRef}
                            type="text"
                            value={dataFetchInput}
                            onChange={(e) => setDataFetchInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addDataFetchFunctions(dataFetchInput);
                              }
                            }}
                            placeholder="Enter function names (comma-separated)"
                            className="flex-1 rounded-md border px-3 py-2 text-sm"
                          />
                          <Button
                            type="button"
                            onClick={() => addDataFetchFunctions(dataFetchInput)}
                            size="sm"
                            variant="outline"
                          >
                            Add
                          </Button>
                        </div>
                        {editing.data_fetch_functions &&
                          editing.data_fetch_functions.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {editing.data_fetch_functions.map((fn, idx) => (
                                <Badge key={idx} variant="secondary" className="gap-1">
                                  {fn}
                                  <button
                                    onClick={() => {
                                      const next = editing.data_fetch_functions?.filter(
                                        (_, i) => i !== idx
                                      );
                                      setEditing({
                                        ...editing,
                                        data_fetch_functions: next,
                                      });
                                    }}
                                    className="ml-1 hover:text-destructive"
                                  >
                                    ×
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              </DetailSection>
            )}

            <DetailSection
              icon={<Layers className="h-5 w-5" />}
              title="Configuration schema"
              subtitle="Define credential fields that users must supply."
            >
              <div className="space-y-3">
                {(editing.config_schema || []).map((field, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col gap-2 rounded-lg border border-dashed border-border/70 bg-muted/20 p-4 sm:flex-row sm:items-center"
                  >
                    <input
                      type="text"
                      value={field.name}
                      onChange={(e) => {
                        const next = [...(editing.config_schema || [])];
                        next[idx] = { ...next[idx], name: e.target.value };
                        setEditing({ ...editing, config_schema: next });
                      }}
                      placeholder="Field name"
                      className="flex-1 rounded-md border px-3 py-2 text-sm"
                    />
                    <select
                      value={field.type || "string"}
                      onChange={(e) => {
                        const next = [...(editing.config_schema || [])];
                        next[idx] = { ...next[idx], type: e.target.value };
                        setEditing({ ...editing, config_schema: next });
                      }}
                      className="rounded-md border px-3 py-2 text-sm"
                    >
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                    </select>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={field.is_secret || false}
                        onChange={(e) => {
                          const next = [...(editing.config_schema || [])];
                          next[idx] = {
                            ...next[idx],
                            is_secret: e.target.checked,
                          };
                          setEditing({ ...editing, config_schema: next });
                        }}
                      />
                      Secret
                    </label>
                    <Button
                      type="button"
                      onClick={() => {
                        const next = editing.config_schema?.filter(
                          (_, i) => i !== idx
                        );
                        setEditing({ ...editing, config_schema: next || [] });
                      }}
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  onClick={() => {
                    const next = [
                      ...(editing.config_schema || []),
                      { name: "", type: "string", is_secret: false },
                    ];
                    setEditing({ ...editing, config_schema: next });
                  }}
                  variant="outline"
                  size="sm"
                  className="self-start"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Field
                </Button>
              </div>
            </DetailSection>

            <DetailSection
              icon={<Info className="h-5 w-5" />}
              title="Suggested prompts"
              subtitle="Surface up to four quick-start ideas for end users."
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Prompt suggestions</span>
                  <span className="text-xs text-muted-foreground">
                    {(Array.isArray(editing.suggested_prompts) ? editing.suggested_prompts.length : 0)}
                    /{MAX_SUGGESTED_PROMPTS}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  These prompts show up wherever this integration provides suggested starting points.
                </p>
                <div className="space-y-3">
                  {editing.suggested_prompts && editing.suggested_prompts.length > 0 ? (
                    editing.suggested_prompts.map((prompt, idx) => (
                      <div key={`suggested-prompt-${idx}`} className="flex gap-2 items-start">
                        <Textarea
                          value={prompt}
                          onChange={(e) => {
                            const next = [...(editing.suggested_prompts || [])];
                            next[idx] = e.target.value;
                            setEditing({ ...editing, suggested_prompts: next });
                          }}
                          rows={2}
                          placeholder="e.g., Summarize open incidents for plant 12"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => {
                            const next = [...(editing.suggested_prompts || [])];
                            next.splice(idx, 1);
                            setEditing({ ...editing, suggested_prompts: next });
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-md border px-3 py-2 text-xs text-muted-foreground">
                      No prompts yet. Use the button below to add your first suggestion.
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={
                    Array.isArray(editing.suggested_prompts) &&
                    editing.suggested_prompts.length >= MAX_SUGGESTED_PROMPTS
                  }
                  onClick={() => {
                    const prompts = Array.isArray(editing.suggested_prompts)
                      ? editing.suggested_prompts
                      : [];
                    if (prompts.length >= MAX_SUGGESTED_PROMPTS) return;
                    setEditing({ ...editing, suggested_prompts: [...prompts, ''] });
                  }}
                >
                  + Add Prompt
                </Button>
              </div>
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: DETAIL_BACKGROUND }}>
      {/* Messages */}
      {(error || success) && (
        <div className="border-b bg-card px-6 py-3">
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-auto">×</button>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>{success}</span>
              <button onClick={() => setSuccess(null)} className="ml-auto">×</button>
            </div>
          )}
        </div>
      )}

      {/* Master-Detail Layout */}
      <MasterDetailLayout
        masterTitle="Integrations"
        masterItems={renderMasterList()}
        detailContent={renderDetailPanel()}
        onNewItem={startNew}
        newItemLabel="New Integration"
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by name, ID, description, or visibility..."
        showSearch={true}
        selectedBackground={DETAIL_BACKGROUND}
      />

      {/* Visibility confirmation */}
      <ConfirmDialog
        open={visibilityConfirmOpen}
        onOpenChange={(open) => {
          setVisibilityConfirmOpen(open);
          if (!open) {
            setPendingVisibility(null);
          }
        }}
        onConfirm={handleVisibilityConfirm}
        title="Publish integration?"
        description="Setting visibility to Public makes this integration visible to every workgroup. Ensure it is ready for broad use before proceeding."
        confirmText="Set to Public"
        cancelText="Keep Current Setting"
        variant="destructive"
      />

      {/* Full Screen Text Modal */}
      {fullScreenModal && (
        <FullScreenTextModal
          isOpen={fullScreenModal.isOpen}
          onClose={() => setFullScreenModal(null)}
          title={fullScreenModal.title}
          content={fullScreenModal.content}
          editable={fullScreenModal.editable}
          onSave={fullScreenModal.onSave}
          onCopy={async () => {
            try {
              if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(fullScreenModal.content);
              }
            } catch (e) {
              console.error('Copy failed:', e);
            }
          }}
        />
      )}
    </div>
  );
};

export default IntegrationManagerNew;
